import { FormEvent, useEffect, useState } from "react";
import { ApiError, PricingTier, PricingTierInput, api } from "../lib/api";
import { useAuth } from "../lib/auth";

interface TierForm {
  name: string;
  slug: string;
  base_price_usd: string;
  included_credits: string;
  overage_rate_usd: string;
  included_storage_gb: string;
  storage_overage_usd_per_gb: string;
  rollover_months: string;
  annual_discount_pct: string;
  sort_order: string;
  is_active: boolean;
  notes: string;
}

const EMPTY_FORM: TierForm = {
  name: "",
  slug: "",
  base_price_usd: "",
  included_credits: "",
  overage_rate_usd: "",
  included_storage_gb: "",
  storage_overage_usd_per_gb: "",
  rollover_months: "0",
  annual_discount_pct: "0",
  sort_order: "0",
  is_active: true,
  notes: "",
};

function toForm(t: PricingTier): TierForm {
  return {
    name: t.name,
    slug: t.slug,
    base_price_usd: String(t.base_price_usd),
    included_credits: String(t.included_credits),
    overage_rate_usd: String(t.overage_rate_usd),
    included_storage_gb: String(t.included_storage_gb),
    storage_overage_usd_per_gb: String(t.storage_overage_usd_per_gb),
    rollover_months: String(t.rollover_months),
    annual_discount_pct: String(t.annual_discount_pct),
    sort_order: String(t.sort_order),
    is_active: t.is_active,
    notes: t.notes ?? "",
  };
}

function toPayload(f: TierForm): PricingTierInput {
  return {
    name: f.name.trim(),
    slug: f.slug.trim(),
    base_price_usd: Number(f.base_price_usd || 0),
    included_credits: Number(f.included_credits || 0),
    overage_rate_usd: Number(f.overage_rate_usd || 0),
    included_storage_gb: Number(f.included_storage_gb || 0),
    storage_overage_usd_per_gb: Number(f.storage_overage_usd_per_gb || 0),
    rollover_months: Number(f.rollover_months || 0),
    annual_discount_pct: Number(f.annual_discount_pct || 0),
    sort_order: Number(f.sort_order || 0),
    is_active: f.is_active,
    notes: f.notes.trim() || null,
  };
}

function money(n: number, dp = 2): string {
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  })}`;
}

function gb(n: number): string {
  // Whole GB above 10, one decimal below it - "0.4 GB" is informative,
  // "1,024.0 GB" is just noise.
  return `${n.toLocaleString(undefined, {
    maximumFractionDigits: n < 10 ? 1 : 0,
  })} GB`;
}

export default function AdminTiers() {
  const me = useAuth((s) => s.user);
  const isOwner = me?.role === "owner";

  const [tiers, setTiers] = useState<PricingTier[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const [editing, setEditing] = useState<PricingTier | "new" | null>(null);
  const [form, setForm] = useState<TierForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [deleteTier, setDeleteTier] = useState<PricingTier | null>(null);
  const [deleting, setDeleting] = useState(false);
  // The 409 from a delete blocked by active customers. Kept separate from the
  // page-level error so it renders inside the confirm modal, next to the
  // button that caused it.
  const [deleteErr, setDeleteErr] = useState<string | null>(null);

  useEffect(() => {
    if (!isOwner) return;
    void load();
  }, [isOwner]);

  async function load() {
    setBusy(true);
    setErr(null);
    try {
      setTiers(await api.get<PricingTier[]>("/admin/tiers"));
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to load pricing tiers");
    } finally {
      setBusy(false);
    }
  }

  function flashFor(msg: string) {
    setFlash(msg);
    setTimeout(() => setFlash((m) => (m === msg ? null : m)), 3500);
  }

  function autoSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 64);
  }

  function openNew() {
    setEditing("new");
    setForm({ ...EMPTY_FORM, sort_order: String(tiers.length + 1) });
    setErr(null);
  }

  function openEdit(t: PricingTier) {
    setEditing(t);
    setForm(toForm(t));
    setErr(null);
  }

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    if (!editing) return;
    setSaving(true);
    setErr(null);
    try {
      if (editing === "new") {
        await api.post<PricingTier>("/admin/tiers", toPayload(form));
        flashFor(`Created "${form.name.trim()}".`);
      } else {
        await api.patch<PricingTier>(`/admin/tiers/${editing.id}`, toPayload(form));
        flashFor(`Updated "${form.name.trim()}".`);
      }
      setEditing(null);
      await load();
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to save tier");
    } finally {
      setSaving(false);
    }
  }

  async function toggleArchive(t: PricingTier) {
    setErr(null);
    try {
      await api.patch<PricingTier>(`/admin/tiers/${t.id}`, {
        is_active: !t.is_active,
      });
      flashFor(
        t.is_active
          ? `Archived "${t.name}". It stays on existing customers but is hidden from new assignments.`
          : `Restored "${t.name}".`,
      );
      await load();
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to change tier state");
    }
  }

  async function confirmDelete() {
    if (!deleteTier) return;
    setDeleting(true);
    setDeleteErr(null);
    try {
      await api.del(`/admin/tiers/${deleteTier.id}`);
      flashFor(`Deleted "${deleteTier.name}".`);
      setDeleteTier(null);
      await load();
    } catch (e: unknown) {
      setDeleteErr(
        e instanceof ApiError ? e.message : "Failed to delete tier",
      );
    } finally {
      setDeleting(false);
    }
  }

  if (!isOwner) {
    return (
      <div className="admin">
        <header className="admin__head">
          <div>
            <div className="page-eyebrow">Billing</div>
            <h1>Pricing tiers</h1>
          </div>
        </header>
        <div className="alert alert--error" role="alert">
          Pricing tiers are restricted to owners.
        </div>
      </div>
    );
  }

  return (
    <div className="admin admin--page-scroll">
      <header className="admin__head">
        <div>
          <div className="page-eyebrow">Billing</div>
          <h1>Pricing tiers</h1>
          <p className="admin__lead">
            Each tier sets a monthly base price, an included credit allowance,
            an included storage allowance, and the rate charged for going past
            either one. Storage bills on the highest daily reading in a period,
            not the closing one. Editing a tier never reprices history: open
            billing periods and past invoices hold their own price snapshots
            and take the new numbers only at the next period boundary.
          </p>
        </div>
        <div className="admin__head-actions">
          <button type="button" className="btn btn--primary" onClick={openNew}>
            + New tier
          </button>
        </div>
      </header>

      {err && <div className="alert alert--error">{err}</div>}
      {flash && <div className="alert alert--ok">{flash}</div>}

      <div className="admin__scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Tier</th>
              <th>Base price</th>
              <th>Included</th>
              <th>Per credit</th>
              <th>Overage rate</th>
              <th>Storage</th>
              <th>Customers</th>
              <th>Measured cost</th>
              <th>Margin</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {busy && (
              <tr>
                <td colSpan={10} className="muted">
                  Loading...
                </td>
              </tr>
            )}
            {!busy && tiers.length === 0 && (
              <tr>
                <td colSpan={10} className="muted">
                  No tiers defined yet.
                </td>
              </tr>
            )}
            {tiers.map((t) => (
              <tr
                key={t.id}
                className={t.is_active ? "" : "admin-table__row--inactive"}
              >
                <td>
                  <div className="admin-table__name">
                    {t.name}
                    {!t.is_active && (
                      <span className="admin-table__chip">archived</span>
                    )}
                  </div>
                  <div className="admin-table__mono muted">{t.slug}</div>
                </td>
                <td className="billing-num">{money(t.base_price_usd)}</td>
                <td className="billing-num">
                  {t.included_credits.toLocaleString()}
                </td>
                <td className="billing-num">
                  {t.effective_per_credit_usd == null ? (
                    <span className="muted">-</span>
                  ) : (
                    money(t.effective_per_credit_usd, 4)
                  )}
                </td>
                <td className="billing-num">{money(t.overage_rate_usd, 4)}</td>
                <td className="billing-num">
                  {gb(t.included_storage_gb)}
                  <div className="muted">
                    {money(t.storage_overage_usd_per_gb, 4)}/GB over
                  </div>
                  {t.org_count > 0 && (
                    <div
                      className="muted"
                      title="Total currently stored by customers on this tier."
                    >
                      {gb(t.stored_gb)} in use
                    </div>
                  )}
                </td>
                <td className="billing-num">
                  {t.org_count === 0 ? (
                    <span className="muted">none</span>
                  ) : (
                    <>
                      {t.org_count}
                      {t.active_org_count !== t.org_count && (
                        <span className="muted">
                          {" "}
                          ({t.active_org_count} active)
                        </span>
                      )}
                    </>
                  )}
                </td>
                <td className="billing-num">
                  {t.cost_per_credit == null ? (
                    <span className="muted" title="No closed invoices on this tier yet.">
                      no data
                    </span>
                  ) : (
                    <>
                      {money(t.cost_per_credit, 4)}
                      {t.unpriced_calls > 0 && (
                        <span
                          className="admin-table__chip admin-table__chip--warn"
                          title={`${t.unpriced_calls} AI call(s) had no rate-card price, so this cost is understated.`}
                        >
                          {t.unpriced_calls} unpriced
                        </span>
                      )}
                    </>
                  )}
                </td>
                <td className="billing-num">
                  {t.margin_multiple == null ? (
                    <span className="muted">-</span>
                  ) : (
                    `${t.margin_multiple.toFixed(2)}x`
                  )}
                </td>
                <td className="admin-table__actions">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => openEdit(t)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => void toggleArchive(t)}
                  >
                    {t.is_active ? "Archive" : "Restore"}
                  </button>
                  <button
                    type="button"
                    className="btn btn--danger"
                    onClick={() => {
                      setDeleteTier(t);
                      setDeleteErr(null);
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create / edit modal */}
      {editing && (
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setEditing(null);
          }}
        >
          <form
            className="modal__card modal__card--lg modal__card--split"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
          >
            <header className="modal__header">
              <div className="modal__header-main">
                <h2 className="modal__header-title">
                  {editing === "new" ? "New pricing tier" : `Edit ${editing.name}`}
                </h2>
              </div>
              <button
                type="button"
                className="modal__close"
                onClick={() => setEditing(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="modal__body">
              <div className="admin-card__grid">
                <div className="admin-card__field admin-card__field--wide">
                  <label htmlFor="tf-name">Tier name</label>
                  <input
                    id="tf-name"
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setForm((f) => ({
                        ...f,
                        name,
                        slug:
                          f.slug === autoSlug(f.name) ? autoSlug(name) : f.slug,
                      }));
                    }}
                  />
                </div>
                <div className="admin-card__field admin-card__field--wide">
                  <label htmlFor="tf-slug">Slug</label>
                  <input
                    id="tf-slug"
                    type="text"
                    required
                    pattern="^[a-z0-9][a-z0-9\-]*$"
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  />
                  <div className="admin-card__hint muted">
                    Lowercase letters, numbers, and hyphens only.
                  </div>
                </div>

                <div className="admin-card__field">
                  <label htmlFor="tf-base">Base price (USD / month)</label>
                  <input
                    id="tf-base"
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={form.base_price_usd}
                    onChange={(e) =>
                      setForm({ ...form, base_price_usd: e.target.value })
                    }
                  />
                </div>
                <div className="admin-card__field">
                  <label htmlFor="tf-credits">Included credits / month</label>
                  <input
                    id="tf-credits"
                    type="number"
                    min="0"
                    step="1"
                    required
                    value={form.included_credits}
                    onChange={(e) =>
                      setForm({ ...form, included_credits: e.target.value })
                    }
                  />
                </div>
                <div className="admin-card__field">
                  <label htmlFor="tf-overage">Overage rate (USD / credit)</label>
                  <input
                    id="tf-overage"
                    type="number"
                    min="0"
                    step="0.000001"
                    required
                    value={form.overage_rate_usd}
                    onChange={(e) =>
                      setForm({ ...form, overage_rate_usd: e.target.value })
                    }
                  />
                </div>

                <div className="admin-card__field">
                  <label htmlFor="tf-storage">Included storage (GB / month)</label>
                  <input
                    id="tf-storage"
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={form.included_storage_gb}
                    onChange={(e) =>
                      setForm({ ...form, included_storage_gb: e.target.value })
                    }
                  />
                  <div className="admin-card__hint muted">
                    Billed on the highest daily reading in the period, so a file
                    deleted before the cycle ends still counts.
                  </div>
                </div>
                <div className="admin-card__field">
                  <label htmlFor="tf-storage-rate">
                    Storage overage (USD / GB)
                  </label>
                  <input
                    id="tf-storage-rate"
                    type="number"
                    min="0"
                    step="0.0001"
                    required
                    value={form.storage_overage_usd_per_gb}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        storage_overage_usd_per_gb: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="admin-card__field">
                  <label htmlFor="tf-rollover">Rollover months</label>
                  <input
                    id="tf-rollover"
                    type="number"
                    min="0"
                    max="12"
                    step="1"
                    value={form.rollover_months}
                    onChange={(e) =>
                      setForm({ ...form, rollover_months: e.target.value })
                    }
                  />
                  <div className="admin-card__hint muted">
                    Recorded for reference. Rollover is not applied yet -
                    allowances currently reset each period.
                  </div>
                </div>
                <div className="admin-card__field">
                  <label htmlFor="tf-annual">Annual discount (%)</label>
                  <input
                    id="tf-annual"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.annual_discount_pct}
                    onChange={(e) =>
                      setForm({ ...form, annual_discount_pct: e.target.value })
                    }
                  />
                  <div className="admin-card__hint muted">
                    Informational. All billing runs monthly.
                  </div>
                </div>
                <div className="admin-card__field">
                  <label htmlFor="tf-sort">Sort order</label>
                  <input
                    id="tf-sort"
                    type="number"
                    step="1"
                    value={form.sort_order}
                    onChange={(e) =>
                      setForm({ ...form, sort_order: e.target.value })
                    }
                  />
                </div>

                <div className="admin-card__field admin-card__field--wide">
                  <label htmlFor="tf-notes">Notes</label>
                  <input
                    id="tf-notes"
                    type="text"
                    maxLength={512}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  />
                </div>
                <div className="admin-card__field admin-card__field--wide">
                  <label className="admin-card__field-toggle">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) =>
                        setForm({ ...form, is_active: e.target.checked })
                      }
                    />{" "}
                    Available for new assignments
                  </label>
                </div>
              </div>
            </div>

            <footer className="modal__footer">
              <span className="muted modal__footer-hint">
                {Number(form.included_credits) > 0
                  ? `Works out to ${money(
                      Number(form.base_price_usd || 0) /
                        Number(form.included_credits),
                      4,
                    )} per included credit.`
                  : "Set an included-credit allowance to see the effective rate."}
              </span>
              <div className="modal__footer-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => setEditing(null)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save tier"}
                </button>
              </div>
            </footer>
          </form>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTier && (
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setDeleteTier(null);
          }}
        >
          <div
            className="modal__card modal__card--split"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="modal__header">
              <div className="modal__header-main">
                <h2 className="modal__header-title">
                  Delete {deleteTier.name}
                </h2>
              </div>
              <button
                type="button"
                className="modal__close"
                onClick={() => setDeleteTier(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="modal__body">
              {deleteErr && <div className="alert alert--error">{deleteErr}</div>}
              <p className="modal__desc">
                Deleting a tier does not affect billing history - past invoices
                keep their own copy of the tier name and prices.
              </p>
              {deleteTier.active_org_count > 0 && (
                <p className="modal__desc muted">
                  {deleteTier.active_org_count} active customer
                  {deleteTier.active_org_count === 1 ? " is" : "s are"} currently
                  on this tier, so the delete will be refused. Deactivate them
                  first, or archive the tier instead to hide it from new
                  assignments.
                </p>
              )}
            </div>

            <footer className="modal__footer">
              <span className="muted modal__footer-hint" />
              <div className="modal__footer-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => setDeleteTier(null)}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    const t = deleteTier;
                    setDeleteTier(null);
                    void toggleArchive(t);
                  }}
                  disabled={deleting || !deleteTier.is_active}
                >
                  Archive instead
                </button>
                <button
                  type="button"
                  className="btn btn--danger"
                  onClick={() => void confirmDelete()}
                  disabled={deleting}
                >
                  {deleting ? "Deleting..." : "Delete tier"}
                </button>
              </div>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
